import fs from "node:fs/promises";
import path from "node:path";
import { parse as parseToml, stringify as stringifyToml } from "smol-toml";
import type { BackendStatus, InstallResult, UninstallResult } from "./types.ts";

const SKAILE_MCP_NAME = "skaile-workspace";

interface CodexMcpEntry {
  command: string;
  args: string[];
  env: Record<string, string>;
}

interface CodexConfig {
  mcp_servers?: Record<string, CodexMcpEntry>;
  [k: string]: unknown;
}

function buildEntry(projectDir: string): CodexMcpEntry {
  return {
    command: "skaile",
    args: ["mcp-server"],
    env: { SKAILE_PROJECT_DIR: projectDir },
  };
}

async function readConfig(p: string): Promise<CodexConfig | null> {
  let raw: string;
  try {
    raw = await fs.readFile(p, "utf-8");
  } catch {
    return null;
  }
  try {
    return parseToml(raw) as CodexConfig;
  } catch (e: unknown) {
    throw new Error(
      `cannot parse ${p}: ${e instanceof Error ? e.message : String(e)}; please fix or remove`,
    );
  }
}

async function writeConfig(p: string, value: CodexConfig): Promise<void> {
  await fs.mkdir(path.dirname(p), { recursive: true });
  // smol-toml's stringify rejects undefined keys; drop empty containers.
  const cleaned: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    if (v === undefined) continue;
    cleaned[k] = v;
  }
  await fs.writeFile(p, stringifyToml(cleaned));
}

function configPath(projectDir: string): string {
  return path.join(projectDir, ".codex", "config.toml");
}

export async function install(projectDir: string): Promise<InstallResult> {
  const p = configPath(projectDir);
  const cfg = (await readConfig(p)) ?? {};
  const next = buildEntry(projectDir);

  const servers = { ...(cfg.mcp_servers ?? {}) };
  const previous = servers[SKAILE_MCP_NAME];
  const matches = previous && JSON.stringify(previous) === JSON.stringify(next);
  let warning: string | undefined;
  if (previous && !matches) {
    warning = `overwrote existing ${SKAILE_MCP_NAME} entry in ${path.relative(projectDir, p)}`;
  }
  if (matches) return { changed: false, method: "toml" };

  servers[SKAILE_MCP_NAME] = next;
  await writeConfig(p, { ...cfg, mcp_servers: servers });
  return { changed: true, method: "toml", warning };
}

export async function uninstall(projectDir: string): Promise<UninstallResult> {
  const p = configPath(projectDir);
  const cfg = await readConfig(p);
  if (!cfg?.mcp_servers || !(SKAILE_MCP_NAME in cfg.mcp_servers)) {
    return { changed: false };
  }
  const { [SKAILE_MCP_NAME]: _removed, ...rest } = cfg.mcp_servers;
  const updated: CodexConfig = { ...cfg, mcp_servers: rest };
  if (Object.keys(rest).length === 0) delete updated.mcp_servers;

  if (Object.keys(updated).length === 0) {
    await fs.unlink(p);
  } else {
    await writeConfig(p, updated);
  }
  return { changed: true };
}

export async function status(projectDir: string): Promise<BackendStatus> {
  const p = configPath(projectDir);
  const cfg = await readConfig(p);
  const installed = !!(cfg?.mcp_servers && SKAILE_MCP_NAME in cfg.mcp_servers);

  if (!installed) {
    return {
      backend: "codex",
      installed: "no",
      enabled: "n/a",
      method: "—",
      location: "—",
    };
  }
  return {
    backend: "codex",
    installed: "yes",
    enabled: "n/a",
    method: "mcp",
    location: path.relative(projectDir, p),
  };
}
